'use client';

import React, { useState } from 'react';
import { useAuth } from '@/doi-tac-module/contexts/AuthContext';
import { AgencyJoinInvitation } from './agency-join-invitation';
import { AgencyRegistrationForm } from './agency-registration-form';
import {
    Clock,
    FileSearch,
    TrendingUp,
    CheckCircle2,
    MessageCircle,
    UserCheck,
    Loader2,
    LogOut,
    Edit3,
    X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useRouter } from 'next/navigation';
import { useLanguage } from '@/doi-tac-module/contexts/LanguageContext';
import { agencyTranslations } from '@/doi-tac-module/constants/agency-translations';

export function AgencyOnboarding({ children }: { children: React.ReactNode }) {
    const { user, isLoggedIn, isLoading, logout, isApprovedAgent } = useAuth();
    const router = useRouter();
    const [isEditing, setIsEditing] = useState(false);
    const { language } = useLanguage();
    const t = agencyTranslations.onboarding.pending;

    if (isLoading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="h-10 w-10 animate-spin text-[#FF6921]" />
            </div>
        );
    }

    if (!isLoggedIn) {
        return <AgencyJoinInvitation />;
    }

    const { partnershipStatus } = user || {};

    if (!partnershipStatus || partnershipStatus === 'UNREGISTERED' || partnershipStatus === 'REJECTED' || (partnershipStatus === 'PENDING' && isEditing)) {
        return (
            <AgencyRegistrationForm
                initialData={user?.agencyProfile}
                onCancel={isEditing ? () => setIsEditing(false) : undefined}
            />
        );
    }

    if (partnershipStatus === 'PENDING') {
        return (
            <div className="container mx-auto px-4 py-20 max-w-4xl">
                <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-100 p-8 md:p-12 text-center animate-in zoom-in-95 duration-500">
                    <div className="w-20 h-20 bg-orange-50 text-[#F2B92A] rounded-full flex items-center justify-center mx-auto mb-6">
                        <Clock className="h-10 w-10 animate-pulse" />
                    </div>

                    <h2 className="text-3xl font-bold font-headline text-[#0D8DC8] mb-4">{t.title[language]}</h2>
                    <p className="text-slate-500 text-lg mb-10 max-w-2xl mx-auto">
                        {t.desc[language]}
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                        <div className="p-6 rounded-2xl bg-slate-50 border border-slate-100 flex flex-col items-center gap-3 group hover:border-[#19A6DF] transition-colors">
                            <FileSearch className="h-8 w-8 text-[#19A6DF]" />
                            <div className="text-sm font-bold text-slate-600 uppercase tracking-wider group-hover:text-[#0D8DC8] text-center">{t.step1.title[language]}</div>
                            <div className="text-xs text-slate-400">{t.step1.desc[language]}</div>
                        </div>
                        <div className="p-6 rounded-2xl bg-slate-50 border border-slate-100 flex flex-col items-center gap-3 group hover:border-[#19A6DF] transition-colors">
                            <TrendingUp className="h-8 w-8 text-[#19A6DF]" />
                            <div className="text-sm font-bold text-slate-600 uppercase tracking-wider group-hover:text-[#0D8DC8] text-center">{t.step2.title[language]}</div>
                            <div className="text-xs text-slate-400">{t.step2.desc[language]}</div>
                        </div>
                        <div className="p-6 rounded-2xl bg-slate-50 border border-slate-100 flex flex-col items-center gap-3 group hover:border-[#19A6DF] transition-colors">
                            <UserCheck className="h-8 w-8 text-[#19A6DF]" />
                            <div className="text-sm font-bold text-slate-600 uppercase tracking-wider group-hover:text-[#0D8DC8] text-center">{t.step3.title[language]}</div>
                            <div className="text-xs text-slate-400">{t.step3.desc[language]}</div>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Button
                            variant="outline"
                            className="gap-2 h-12 px-8 rounded-full border-slate-200 hover:text-[#0D8DC8] hover:border-[#0D8DC8]"
                            onClick={() => setIsEditing(true)}
                        >
                            <Edit3 className="h-4 w-4" /> {t.buttons.edit[language]}
                        </Button>
                        <Button
                            variant="outline"
                            className="gap-2 h-12 px-8 rounded-full border-slate-200 hover:text-[#0D8DC8] hover:border-[#0D8DC8]"
                            onClick={() => { logout(); router.push('/doi-tac'); }}
                        >
                            <LogOut className="h-4 w-4" /> {t.buttons.logout[language]}
                        </Button>
                        <Button
                            className="bg-[#F2B92A] hover:bg-[#F2B92A]/90 text-white gap-2 h-12 px-8 rounded-full font-bold shadow-lg"
                            onClick={() => window.location.reload()}
                        >
                            <MessageCircle className="h-4 w-4" /> {t.buttons.contact[language]}
                        </Button>
                    </div>

                    <div className="mt-12 pt-8 border-t border-slate-100 flex items-center justify-center gap-2 text-slate-400 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-[#AFC536]" />
                        {t.footer[language]}
                    </div>
                </div>
            </div>
        );
    }

    if (isApprovedAgent) {
        return <>{children}</>;
    }

    return <AgencyJoinInvitation />;
}
